import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, GraduationCap, Trophy, ChevronRight, Users,
  Library, Gamepad2, FileText, PenTool, ArrowLeft
} from 'lucide-react';
import { getStageFromYear } from '../config/taxonomy';
import PromotionalBanner from '../components/PromotionalBanner';

const ExploreLanding = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const userName = progress?.user?.name?.split(' ')[0] || 'Learner';
  const subscription = progress?.user?.subscription;
  
  // Get user's year group from profile
  const userYearGroup = progress?.user?.studentProfile?.yearGroup || 
                        progress?.user?.yearGroup || 
                        null;

  // Get user's stage for filtering
  const userStage = useMemo(() => {
    if (!userYearGroup) return null;
    return getStageFromYear(userYearGroup);
  }, [userYearGroup]);

  // Feature cards configuration
  const features = [
    {
      id: 'ed-library',
      title: "Ed's English Library",
      description: 'Reading passages, vocabulary builders, and comprehension exercises',
      icon: BookOpen,
      color: 'from-green-600 to-emerald-600',
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-600',
      path: '/elibrary',
      badge: '30 Passages'
    },
    {
      id: 'practice-library',
      title: 'Practice Test Library',
      description: 'Practice papers across all subjects for your year group',
      icon: Library,
      color: 'from-blue-600 to-indigo-600',
      bgColor: 'bg-blue-500/10',
      textColor: 'text-blue-600',
      path: '/practice-library',
      badge: 'Popular'
    },
    {
      id: 'challenger-mode',
      title: 'Challenger Series',
      description: 'Unlimited adaptive practice with smart difficulty adjustment',
      icon: Gamepad2,
      color: 'from-purple-600 to-pink-600',
      bgColor: 'bg-purple-500/10',
      textColor: 'text-purple-600',
      path: '/challenger-hub',
      badge: "Ed's Picks"
    },
    {
      id: 'mock-exams',
      title: 'Mock Exams',
      description: 'Full-length timed mock papers for exam preparation',
      icon: FileText,
      color: 'from-amber-600 to-orange-600',
      bgColor: 'bg-amber-500/10',
      textColor: 'text-amber-600',
      path: '/assessment-factory',
      badge: 'Timed'
    },
    {
      id: 'whiteboard-x',
      title: 'Whiteboard X',
      description: 'Weekly challenges and community competitions',
      icon: PenTool,
      color: 'from-orange-600 to-red-600',
      bgColor: 'bg-orange-500/10',
      textColor: 'text-orange-600',
      path: '/whiteboard-x',
      badge: 'Coming Soon',
      disabled: true
    }
  ];

  // Get display name for user's exam level
  const getExamLevelDisplay = () => {
    if (!userStage) return null;
    
    const stageNames = {
      'ks1': '7+ Entry',
      '7-plus': '7+ Entry',
      'ks2-lower': '11+ Entry',
      'ks2-upper': '11+ Entry',
      '11-plus': '11+ Entry',
      'ks3': 'Year 9',
      'ks4': 'GCSE',
      'a-level': 'A-Level'
    };
    
    return stageNames[userStage] || userYearGroup;
  };

  const examLevel = getExamLevelDisplay();

  return (
    <div 
      data-testid="explore-landing"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white"
    >
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/welcome')}
            className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {examLevel && (
            <Badge className="bg-violet-500/20 text-violet-600 border-violet-500/30">
              <GraduationCap className="w-3 h-3 mr-1" />
              {examLevel}
            </Badge>
          )}
        </div>

        {/* Promotional Banner for new users */}
        {subscription?.isPromotional && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <PromotionalBanner subscription={subscription} userName={userName} />
          </motion.div>
        )}

        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Learning Tools
          </h1>
          <p className="text-slate-500">
            Browse practice materials, reading resources, and exam prep tools
            {userYearGroup && <span className="text-violet-600"> for {userYearGroup}</span>}
          </p>
        </motion.div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isDisabled = feature.disabled;
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  data-testid={`feature-${feature.id}`}
                  className={`bg-white border-slate-200 transition-all h-full ${
                    isDisabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:border-slate-200 cursor-pointer group'
                  }`}
                  onClick={() => !isDisabled && navigate(feature.path)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2.5 rounded-xl ${feature.bgColor} ${isDisabled ? 'opacity-60' : ''}`}>
                        <Icon className={`w-5 h-5 ${feature.textColor}`} />
                      </div>
                      {feature.badge && (
                        <Badge variant="secondary" className={`text-xs ${
                          isDisabled 
                            ? 'bg-gray-500/20 text-slate-500' 
                            : 'bg-slate-50 text-slate-600'
                        }`}>
                          {feature.badge}
                        </Badge>
                      )}
                    </div>
                    
                    <h3 className={`text-base font-semibold mb-1.5 transition-colors ${
                      isDisabled ? 'text-slate-500' : 'text-slate-900 group-hover:text-violet-600'
                    }`}>
                      {feature.title}
                    </h3>
                    
                    <p className={`text-sm mb-3 line-clamp-2 ${isDisabled ? 'text-slate-500' : 'text-slate-500'}`}>
                      {feature.description}
                    </p>
                    
                    <div className={`flex items-center text-sm font-medium ${
                      isDisabled 
                        ? 'text-slate-500' 
                        : 'text-violet-600 group-hover:translate-x-1'
                    } transition-transform`}>
                      {isDisabled ? 'Not available in trial' : 'Open'} 
                      {!isDisabled && <ChevronRight className="w-4 h-4 ml-1" />}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Stats - Simplified */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 grid grid-cols-3 gap-4"
        >
          {[
            { label: 'Practice Papers', value: '5,000+', icon: FileText },
            { label: 'Reading Passages', value: '30+', icon: BookOpen },
            { label: 'Success Rate', value: '94%', icon: Trophy }
          ].map((stat, i) => (
            <Card key={i} className="bg-white border-slate-200">
              <CardContent className="p-4 text-center">
                <stat.icon className="w-5 h-5 text-violet-600 mx-auto mb-1.5" />
                <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ExploreLanding;
